<!-- eslint-disable vue/no-v-html -->
<template>
  <ul class="article-header" :class="{ 'has-appear': hasAppear }">
    <li
      v-for="(item, i) in content"
      :ref="el => (itemsRef[i] = el)"
      :key="item.label"
      class="article-header__item-outer"
    >
      <a
        v-if="item.link"
        :key="item.link"
        :href="item.link.url || item.link"
        target="_blank"
        :class="{ 'is-link': item.link }"
        :title="item.label"
        class="article-header__item"
      >
        <figure v-if="item.picture" class="article-header__item__picture">
          <img
            :src="item.picture.src"
            :alt="item.picture.alt"
            sizes="200px"
            :srcset="sets(item.picture)"
          />
        </figure>
        <GCtaRounded
          v-else
          :text="chrome.language === 'en' ? 'Link' : 'Lien'"
          :repeat="3"
          :offset="16"
          class="article-header__item__icon"
          name="read-case"
          size="small"
          :foreground="foreground"
          :background="background"
        />
        <dl class="article-header__item__content">
          <dt class="info-label article__header__item__label">
            <span>{{ item.label }}</span>
          </dt>
          <dd v-if="item.link" class="info-value article__header__item__value">
            <span>{{ item.name || item.value || item.link.label }}</span>
          </dd>
        </dl>
      </a>

      <dl v-else :key="i" class="article-header__item">
        <figure v-if="item.picture" class="article-header__item__picture">
          <img
            :src="item.picture.src"
            :alt="item.picture.alt"
            sizes="200px"
            :srcset="sets(item.picture)"
          />
        </figure>
        <div>
          <dt class="info-label article__header__item__label">
            <span>{{ item.label }}</span>
          </dt>
          <template v-if="Array.isArray(item.value)">
            <dd
              v-for="val in item.value"
              :key="val"
              class="info-value article__header__item__value"
            >
              <span>{{ val }}</span>
            </dd>
          </template>
          <dd v-else class="info-value article__header__item__value">
            <span
              v-if="item.value || item.name"
              v-html="item.value || item.name"
            ></span>
          </dd>
        </div>
      </dl>
      <!-- </template> -->
    </li>
  </ul>
</template>

<script setup lang="ts">
import gsap from 'gsap'
import { ref, onMounted } from 'vue'

import { getValue } from '@/modules/colors'
import { useChromeStore } from '@/stores/chrome'
const chrome = useChromeStore()
import { Picture, RefsArray } from '@/types'

defineProps({
  content: {
    type: Object,
    required: true,
  },
})

const sets = (picture: Picture) =>
  Object.keys(picture.sets).reduce(
    (prev, current) =>
      // eslint-disable-next-line prefer-template
      `${prev ? prev + ' ' : ''}${picture.sets[current]} ${current}w,`,
    ''
  )

const foreground = ref('')
const background = ref('')
const hasAppear = ref(false)
const itemsRef = ref<RefsArray>([])

let appearTl: gsap.core.Timeline

const play = () => appearTl?.play().then()

defineExpose({
  itemsRef,
  play,
})

onMounted(() => {
  foreground.value = getValue('foreground', document.documentElement)
  background.value = getValue('background', document.documentElement)

  if (itemsRef.value.length > 0) {
    const items = itemsRef.value.map(item => {
      const el = item as Element
      const labelParent = el.querySelector('.info-label')
      const res = {} as { label: Element; values: Element[] }

      const labelEl = labelParent?.querySelector('span, a')

      if (labelEl) {
        res.label = labelEl
      }

      const valueParents = el.querySelectorAll('.info-value')

      if (valueParents.length > 0) {
        const valueEls = [...valueParents]
          .map(el => el.querySelector('span, a'))
          .filter(el => el !== null) as Element[]

        res.values = valueEls
      }

      return res
    })

    appearTl = gsap
      .timeline({
        paused: true,
        onStart() {
          hasAppear.value = true
        },
      })
      .add('start')

    items.forEach((item, i) => {
      const items = [item.label].concat(item.values)
      appearTl.from(
        items,
        {
          yPercent: -100,
          opacity: 0,

          duration: 0.5,
          stagger: 0.1,
          ease: 'power4.out',
        },
        `start+=${i * 0.1}`
      )
    })
  }
})
</script>

<style lang="scss" scoped>
$bp: s;

.article-header {
  display: grid;
  grid-template-rows: repeat(2, auto);
  grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
  align-items: flex-start;
  margin-top: 0;
  margin-right: 0;
  margin-left: 0;
  padding: 0;
  border: solid transparent;
  border-width: 0 0 1px;
  transition: border-color 0.3s ease-out;

  &.has-appear {
    border-color: var(--c-foreground);
  }

  @include mq($bp) {
    margin-top: 2rem;
    border-width: 0;
  }
}

.article-header__item-outer {
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0;
  padding: 1.2rem 0;
  list-style-type: none;
  transition: border-color 0.3s ease-out;

  :deep(strong) {
    display: block;
  }

  .has-appear & {
    border-color: var(--c-foreground);
  }

  @include mq($bp) {
    &:nth-child(n) {
      padding: 0;
      border: 0;
    }
  }
}

.article-header__item {
  display: flex;
  flex-grow: 1;
  color: var(--c-foreground);

  div {
    display: flex;
    flex-direction: column;
  }

  &.is-link {
    display: flex;
  }
}

.article-header__item__picture-outer {
  display: flex;
}

.article-header__item__picture {
  // prettier-ignore
  @include fluid(width,(xxs: 30px, xxl: 50px));
  // prettier-ignore
  @include fluid(height,(xxs: 30px, xxl: 50px));
  // prettier-ignore
  @include fluid(min-width,(xxs: 30px, xxl: 50px));

  overflow: hidden;
  margin: 0 calc(1rem + 6px) 0 0;
  border-radius: 50%;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
}

.article-header__item__icon {
  width: 4rem;
  height: 4rem;
  margin-right: 2rem;
  opacity: 0;
  transition: opacity 0.3s ease-out;

  :deep(svg) {
    /* stylelint-disable declaration-no-important */
    width: 100% !important;
    height: 100% !important;
    /* stylelint-enable declaration-no-important */
  }

  .has-appear & {
    opacity: 1;
  }

  @include mq($bp) {
    display: none !important;
  }
}

.article__header__item__label,
.article__header__item__value {
  display: block;
}

.article__header__item__label {
  margin-bottom: 0.25em;
  opacity: 0.5;
}

.article__header__item__value {
  :deep(a) {
    color: var(--c-foreground);
  }

  &:hover {
    :deep(a) {
      opacity: 0.5;
    }
  }
}

.info-label,
.info-value {
  position: relative;
  overflow: hidden;
}
</style>
